import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SaveAsOutlinedIcon from '@mui/icons-material/SaveAsOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { PROGRAMIZ, programizSecondaryButtonSx } from './programizTheme';

function FileMenuItem({ icon, label, shortcut, onClick, disabled }) {
  return (
    <MenuItem onClick={onClick} disabled={disabled} sx={{ fontSize: 13, py: 1 }}>
      <Box component="span" sx={{ mr: 1.5, display: 'flex', color: PROGRAMIZ.muted }}>
        {icon}
      </Box>
      <Box component="span" sx={{ flex: 1 }}>
        {label}
      </Box>
      {shortcut ? (
        <Box component="span" sx={{ ml: 2, color: PROGRAMIZ.muted, fontSize: 12 }}>
          {shortcut}
        </Box>
      ) : null}
    </MenuItem>
  );
}

export default function ProgramizFileMenu({
  busy = false,
  onNew,
  onOpen,
  onSave,
  onSaveAs,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const closeMenu = () => setMenuAnchor(null);

  const run = (handler) => () => {
    // Call handler first so File System Access pickers keep the user gesture.
    handler?.();
    closeMenu();
  };

  const menuItems = (
    <>
      <FileMenuItem
        icon={<NoteAddOutlinedIcon fontSize="small" />}
        label="New"
        shortcut="Ctrl+N"
        onClick={run(onNew)}
        disabled={busy}
      />
      <FileMenuItem
        icon={<FolderOpenOutlinedIcon fontSize="small" />}
        label="Open"
        shortcut="Ctrl+O"
        onClick={run(onOpen)}
        disabled={busy}
      />
      <FileMenuItem
        icon={<SaveOutlinedIcon fontSize="small" />}
        label="Save"
        shortcut="Ctrl+S"
        onClick={run(onSave)}
        disabled={busy}
      />
      <FileMenuItem
        icon={<SaveAsOutlinedIcon fontSize="small" />}
        label="Save As"
        shortcut="Ctrl+Shift+S"
        onClick={run(onSaveAs)}
        disabled={busy}
      />
    </>
  );

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
      <Button
        size="small"
        variant="outlined"
        endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: '16px !important' }} />}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        disabled={busy}
        sx={{
          ...programizSecondaryButtonSx,
          display: { xs: 'inline-flex', md: 'none' },
          minWidth: 0,
          px: 1.25,
        }}
      >
        File
      </Button>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ display: { xs: 'none', md: 'flex' } }}
      >
        <Tooltip title="Open (Ctrl+O)">
          <span>
            <IconButton
              size="small"
              onClick={onOpen}
              disabled={busy}
              aria-label="Open file"
              sx={{ color: PROGRAMIZ.muted, '&:hover': { color: PROGRAMIZ.text } }}
            >
              <FolderOpenOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Save (Ctrl+S)">
          <span>
            <IconButton
              size="small"
              onClick={onSave}
              disabled={busy}
              aria-label="Save file"
              sx={{ color: PROGRAMIZ.muted, '&:hover': { color: PROGRAMIZ.text } }}
            >
              <SaveOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Save As (Ctrl+Shift+S)">
          <span>
            <IconButton
              size="small"
              onClick={onSaveAs}
              disabled={busy}
              aria-label="Save file as"
              sx={{ color: PROGRAMIZ.muted, '&:hover': { color: PROGRAMIZ.text } }}
            >
              <SaveAsOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="New (Ctrl+N)">
          <span>
            <IconButton
              size="small"
              onClick={onNew}
              disabled={busy}
              aria-label="New file"
              sx={{ color: PROGRAMIZ.muted, '&:hover': { color: PROGRAMIZ.text } }}
            >
              <NoteAddOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            minWidth: 220,
            mt: 0.5,
            border: `1px solid ${PROGRAMIZ.border}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          },
        }}
      >
        {menuItems}
      </Menu>
    </Stack>
  );
}
